<template>
  <div class="app-container">
    <!-- 首屏 -->
    <the-hero :active-tab="currentActiveTab" @tab-click="scrollToModule" @login="openLogin" />
    <!-- 签到福利 -->
    <the-signin
      ref="signin"
      id="signin"
      :data-source="signinList"
      @login="openLogin"
      @show-records="showRecords"
    />
    <!-- 组队赢好礼 -->
    <the-team ref="team" id="team" @login="openLogin" @go-lottery="goToLottery" />
    <!-- 幸运大转盘 -->
    <the-lottery
      ref="lottery"
      id="lottery"
      :lottery-data="lotteryData"
      @login="openLogin"
      @bind-address="showAddress"
      @show-records="showRecords"
      @go-team="goToTeam"
    />
    <!-- 更多福利 -->
    <the-community v-if="!isChannel" :data-source="communityList" />
    <!-- 登录 -->
    <the-login ref="login" />
    <!-- 我的奖励 -->
    <the-prize-records ref="records" @bind-address="showAddress" />
    <!-- 收货地址 -->
    <the-bind-address ref="address" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TheHero from '@/components/TheHero'
import TheCommunity from '@/components/TheCommunity'
import TheSignin from '@/components/TheSignin'
import TheTeam from '@/components/TheTeam'
import TheLottery from '@/components/TheLottery'
import TheLogin from '@/components/TheLogin'
import ThePrizeRecords from '@/components/ThePrizeRecords'
import TheBindAddress from '@/components/TheBindAddress'
import {
  fetchConfig,
  fetchLocalContent,
  fetchUserInfo,
  fetchSigninInfo,
  fetchTeamInfo
} from '@/api'
import { getQueryString } from '@/utils'

export default {
  name: 'App',
  components: {
    TheHero,
    TheCommunity,
    TheSignin,
    TheTeam,
    TheLottery,
    TheLogin,
    ThePrizeRecords,
    TheBindAddress
  },
  data() {
    return {
      communityList: [],
      signinList: [],
      lotteryData: {},
      currentActiveTab: 0, // 初始状态设为0，表示没有选中任何tab
      scrollTimer: null
    }
  },
  computed: {
    ...mapGetters(['token', 'isDebug', 'isChannel']),
    baseColumnId() {
      return Number(process.env.VUE_APP_DATA_BASE)
    },
    signinColumnId() {
      return Number(process.env.VUE_APP_DATA_SIGNIN)
    },
    teamColumnId() {
      return Number(process.env.VUE_APP_DATA_TEAM)
    },
    lotteryColumnId() {
      return Number(process.env.VUE_APP_DATA_LOTTERY)
    },
    communityColumnId() {
      return Number(process.env.VUE_APP_DATA_COMMUNITY)
    }
  },
  mounted() {
    this.initConfig()
    this.initLocal()

    // 添加滚动监听
    this.addScrollListener()
  },
  beforeDestroy() {
    // 移除滚动监听
    this.removeScrollListener()
  },
  methods: {
    ...mapActions([
      'setConfig',
      'setLocal',
      'setUser',
      'updateAppData',
      'setSigninInfo',
      'setTeamInfo'
    ]),

    // 添加滚动监听
    addScrollListener() {
      window.addEventListener('scroll', this.handleScroll, { passive: true })
    },

    // 移除滚动监听
    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    // 处理滚动事件
    handleScroll() {
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }

      this.scrollTimer = setTimeout(() => {
        this.updateActiveTab()
      }, 100)
    },

    // 更新当前激活的tab
    updateActiveTab() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight

      // 获取各个模块的位置
      const signinEl = document.getElementById('signin')
      const teamEl = document.getElementById('team')
      const lotteryEl = document.getElementById('lottery')

      if (!signinEl || !teamEl || !lotteryEl) return

      const signinTop = signinEl.offsetTop
      const teamTop = teamEl.offsetTop - 50 // 考虑偏移量
      const lotteryTop = lotteryEl.offsetTop - 50 // 考虑偏移量

      // 判断当前在哪个模块
      // 首屏时（scrollTop很小）不激活任何tab
      if (scrollTop < 100) {
        this.currentActiveTab = 0 // 首屏时不激活任何tab
      } else if (scrollTop + windowHeight * 0.3 < teamTop) {
        this.currentActiveTab = 1 // 周年签到
      } else if (scrollTop + windowHeight * 0.3 < lotteryTop) {
        this.currentActiveTab = 2 // 组队
      } else {
        this.currentActiveTab = 3 // 抽奖
      }
    },

    // 滚动到指定模块
    scrollToModule(tabIndex) {
      let targetEl = null
      let offset = 0

      switch (tabIndex) {
        case 1:
          targetEl = document.getElementById('signin')
          offset = 0 // 第一个模块不需要偏移
          break
        case 2:
          targetEl = document.getElementById('team')
          offset = 50 // 第二个模块向下偏移50px
          break
        case 3:
          targetEl = document.getElementById('lottery')
          offset = 50 // 第三个模块向下偏移50px
          break
      }

      if (targetEl) {
        // 计算目标滚动位置
        const targetPosition = targetEl.offsetTop - offset

        // 使用window.scrollTo实现精确的偏移滚动
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        })

        this.currentActiveTab = tabIndex
      }
    },
    async initConfig() {
      const { data } = await fetchConfig()
      this.setConfig(data || {})
    },
    async initLocal() {
      const { data } = await fetchLocalContent({
        column_id: `${this.baseColumnId},${this.signinColumnId},${this.teamColumnId},${this.lotteryColumnId},${this.communityColumnId}`
      })
      this.setLocal({
        // 基础配置
        base:
          (data[this.baseColumnId] &&
            data[this.baseColumnId].length &&
            data[this.baseColumnId][data[this.baseColumnId].length - 1].content) ||
          {},
        // 组队活动配置
        team:
          (data[this.teamColumnId] &&
            data[this.teamColumnId].length &&
            data[this.teamColumnId][data[this.teamColumnId].length - 1].content) ||
          {}
      })

      // 官方入口列表
      const communityList = data[this.communityColumnId] || []
      this.communityList = communityList.map(el => el.content)

      // 签到福利奖励配置
      const signinList = data[this.signinColumnId] || []
      this.signinList = signinList.map(el => el.content)

      // 抽奖配置数据
      let lotteryData = data[this.lotteryColumnId] || []
      lotteryData = lotteryData[lotteryData.length - 1] || {}
      this.lotteryData = lotteryData.content

      // 当token存在时请求用户数据
      if (this.token) {
        this.initUser()
      } else {
        this.setUser({})
      }
    },
    async initUser() {
      const { data = {} } = await fetchUserInfo()
      if (Object.keys(data).length > 0) {
        // 设置用户基本信息
        this.setUser(data)
        // 设置活动相关数据
        this.updateAppData(data)
        // 初始化签到信息
        this.initSignin()
        // 初始化队伍信息
        this.initTeam()
        // 设置前端统计用户名
        if (this.$bl) {
          this.$bl.setConfig({
            setUsername: () => {
              return String(data.phone)
            }
          })
        }
      }
    },
    async initSignin() {
      const { data = {} } = await fetchSigninInfo(
        this.isDebug && { test_time: getQueryString('t') }
      )
      if (Object.keys(data).length > 0) {
        this.setSigninInfo(data)
      }
    },
    async initTeam() {
      const { data = {} } = await fetchTeamInfo()
      this.setTeamInfo(data)
    },
    /* 登录 */
    openLogin() {
      this.$refs.login.show()
    },
    /** 我的奖励 */
    showRecords() {
      this.$refs.records.show()
    },
    /** 收货地址 */
    showAddress(prizeName) {
      this.$refs.address.show(prizeName)
    },
    /** 前往抽奖 */
    goToLottery() {
      this.scrollToModule(3)
    },
    /** 前往组队 */
    goToTeam() {
      this.scrollToModule(2)
    }
  }
}
</script>
